import { GeneralsMealPreferencesMealType } from "@/clients/models/GeneralsMealPreferencesMealType";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { AnimatedOnboardingContainer } from "../../components/animated-onboarding-container";
import { useMealTypePreference } from "../../hooks/use-meal-type-preference-lifestyle";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const LIFESTYLE_OPTIONS = [
  {
    id: "quick-easy" as const,
    value: GeneralsMealPreferencesMealType.mealTypePreference.QUICK_AND_EASY,
    label: "Quick & easy",
    description:
      "Includes simple meals like bangers and mash, porridge pots, sandwiches, and easy recipes.",
  },
  {
    id: "cook-from-scratch" as const,
    value: GeneralsMealPreferencesMealType.mealTypePreference.COOK_FROM_SCRATCH,
    label: "Cook from scratch",
    description:
      "For those who have a bit more time - fresh recipes cooked from scratch that could be a little more involved.",
  },
  {
    id: "bit-of-both" as const,
    value: GeneralsMealPreferencesMealType.mealTypePreference.BOTH,
    label: "A bit of both",
    description: "A mix of both - some days you've got time, other days you haven't.",
  },
] as const;

type LifestyleOptionId = (typeof LIFESTYLE_OPTIONS)[number]["id"];

const OnboardingLifestyle = () => {
  const { info, navigateToNextStep } = useStepRouteInfo();
  const { showError } = useToast();

  const { updateMealTypePreferenceAsync, isUpdating } = useMealTypePreference();

  const handleOptionSelect = async (optionId: LifestyleOptionId) => {
    if (isUpdating) return;

    const option = LIFESTYLE_OPTIONS.find((opt) => opt.id === optionId);
    if (!option) return;

    try {
      await updateMealTypePreferenceAsync({
        mealTypePreference: option.value,
      });
      setSmartlookOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      setMixpanelOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      navigateToNextStep();
    } catch (error) {
      console.error(error);
      showError("Failed to save lifestyle preference. Please try again.");
    }
  };

  return (
    <AnimatedOnboardingContainer
      title="Okay, what type of meals make your life easier?"
      classNames={{
        container: "flex-1 h-full flex flex-col",
        form: "flex-1 flex flex-col justify-center",
      }}
    >
      <div className="flex w-full flex-1 flex-col justify-center gap-[18px]">
        {LIFESTYLE_OPTIONS.map((option) => (
          <div key={option.id} className="flex flex-col gap-3">
            <OutlinedButton
              color="black"
              bgColor="white"
              textCase="normal"
              onClick={() => handleOptionSelect(option.id)}
              disabled={isUpdating}
            >
              {option.label}
            </OutlinedButton>

            <p className={cn(textVariants.footnote, "w-full text-black")}>{option.description}</p>
          </div>
        ))}
      </div>
    </AnimatedOnboardingContainer>
  );
};

export default OnboardingLifestyle;
